/**
  * @class meleGroup
  * @description
  *
  * # Purpose
  * Implements `<mele-group>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-group></mele-group>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-group',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleContainer from 'meleContainer'

const PROPERTIES = _.extend({}, {
  width: { type: 'string', description: "Sets the width of the element in the window, regardles of it's type. Options for width: 1, 1/2, 1/3, 1/4" },
  header: { type: 'string', description: 'header of group' },
  footer: { type: 'string', description: 'footer of group' },
  label: { type: 'string', description: 'primary label (used when included in <mele-tabs>)' },
  label2: { type: 'string', description: 'sublabel (used when included in <mele-tabs>)' },
  icon: { type: 'string', description: 'icon (used when included in <mele-tabs>)' },
  count: { type: 'string', description: 'count of items in group' },
  collapsible: { description: 'indicates if group can be collapse', type: 'boolean' },
  collapsed: { description: 'indicates if group is collapsed', type: 'boolean' },
  keyhasvalue: { type: 'string', description: 'hides group, if child with string has falsy value' },
  show: { type: 'array', description: 'shows element, if array content is evaluated to true' },
  vars: { type: 'string', description: 'used to store variables dependent on input fields' },
}, meleContainer._properties)

export default class meleGroup extends meleContainer {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    super.init()

    // insert wrapper around the whole window, putting it as the first child
    this._wrapper = this.prependChildren({ tag: 'mele-wrapper' })
    this._wrapper.classList.add(this.classScope('wrapper'))

    // insert header with wrapper and ensure they are the first
    this._header_wrapper = this._wrapper.prependChildren({ tag: 'mele-header-wrapper' })
    this._header_wrapper.classList.add(this.classScope('header-wrapper'))
    this._header = this._header_wrapper.prependChildren({ tag: 'mele-header' })
    this._header.classList.add(this.classScope('header'))
    this._header.onclick = ()=>{
      if(this.collapsible) {
        this.collapsed = !this.collapsed
      }
    }

    // insert body with wrapper
    this._body_wrapper = this._wrapper.appendChildren({ tag: 'mele-body-wrapper' })
    this._body_wrapper.classList.add(this.classScope('body-wrapper'))
    this._body = this._body_wrapper.appendChildren({ tag: 'mele-body' })
    this._body.classList.add(this.classScope('body'))

    // insert footer with wrapper and ensure they are the first
    this._footer_wrapper = this._wrapper.appendChildren({ tag: 'mele-footer-wrapper' })
    this._footer_wrapper.classList.add(this.classScope('footer-wrapper'))
    this._footer = this._footer_wrapper.prependChildren({ tag: 'mele-footer' })
    this._footer.classList.add(this.classScope('footer'))

    // move all other already existing subelements into body
    while(this.childNodes.length>1) {
      const node = this.childNodes[1] // take second child and move it where appropriate
      this._body.appendChild(node)
    }
  } // init

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "hide"
  */
  showChanged(value) {
    if(!Array.isArray(value) || value.length !== 2) return
    const [key, val] = value

    const holder = this.closest('.mele-container:not([propagate])')
    const el = holder.find('@{'+key+'}')
    const real_val = el ? el.value : null

    if(_.isBoolean(val)) {
      this.hidden = real_val
    } else {
      this.hidden = real_val != val
    }
  } // hideChanged

  collapsedChanged(value, old) {
    if(old && !value) {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' })
      // this.scrollTop = this.scrollHeight;
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "header"
  */
  headerChanged() {
    const html = this.translateAndSetDependencies(this.header, false, 'header')
    this._header.innerHTML = html
  } // headerChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "header"
  */
  footerChanged() {
    const html = this.translateAndSetDependencies(this.footer, false, 'footer')
    this._footer.innerHTML = html
  } // footerChanged

  disabledChanged() {
    const elTabs = this.closest('.mele-tabs')
    if(elTabs) elTabs.refreshTabs()
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "label"
  */
  labelChanged() {
    const elTabs = this.closest('.mele-tabs')
    if(elTabs) elTabs.refreshTabs()
  } // labelChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "label2"
  */
  label2Changed() {
    const elTabs = this.closest('.mele-tabs')
    if(elTabs) elTabs.refreshTabs()
  } // label2Changed

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "icon"
  */
  iconChanged() {
    const elTabs = this.closest('.mele-tabs')
    if(elTabs) elTabs.refreshTabs()
  } // iconChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "count"
  */
  countChanged() {
    const elTabs = this.closest('.mele-tabs')
    if(elTabs) elTabs.refreshTabs()
  } // countChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "key"; same effect as changed language; will refresh list of items
  */
  keyChanged() {
    this.invokeDependentChanges('key')
    const elTabs = this.closest('.mele-tabs')
    if(elTabs) elTabs.refreshTabs()
  } // keyChanged

  keyhasvalueChanged(value) {
    if(value) {
      let [key, attr] = value.split(':')
      attr = attr || 'value'

      this._groupObserver = new MutationObserver(()=>{
        const el = this.find('@{'+key+'}')
        if(el) {
          // hide element, because maybe data will not arrive
          this.hidden = true
          this._elObserver = new MutationObserver(()=>{
            // when data come, and are set, we will show element
            if(el && !_.isEmpty(el[attr])) {
              this.hidden = false
            }
          })
          this._elObserver.observe(el, { attrubutes: true, attributeFilter: [attr], childList: false, characterData: false })
        }
      })
      this._groupObserver.observe(this._body, { attributes: false, childList: true, characterData: false })
    }
    const elTabs = this.closest('.mele-tabs')
    if(elTabs) elTabs.refreshTabs()
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "vars"
  */
  varsChanged() {
    this.setAttribute('tvars', this.translateAndSetDependencies(this.vars, false, 'vars'))
  } // varsChanged
} // class meleGroup

meleGroup._properties = PROPERTIES
meleGroup.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-group', meleGroup)
